import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  API_ASSET_STATUS_UPDATES,
  API_ASSET_STATUS_UPDATES_EXPORT,
} from "../../utils/apis";
import { CAN_UPDATE_ASSET_STATUS } from "../../utils/constants";
import {
  createViewAction,
  formatDate,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_ASSET_STATUS_UPDATES } from "../../utils/urls";
import {
  ASSET_STATUSES,
  AssetStatusTag,
  AssetSummary,
  assetExport,
  assetLabel,
  auditFields,
  createdAtFilter,
  isDisposed,
  toFilterOptions,
  toSelectItems,
  useAssetPermissions,
  useAssetPicker,
} from "./assetUtils";

export const useAssetStatusUpdates = () => {
  const can = useAssetPermissions();

  const tableColumns = () => [
    { id: "status_update_id", header: "Reference" },
    { id: "asset", header: "Asset", filter: { type: "text" } },
    { id: "previous_status", header: "Previous Status" },
    {
      id: "new_status",
      header: "New Status",
      filter: { type: "select", options: toFilterOptions(ASSET_STATUSES) },
    },
    { id: "reason", header: "Reason" },
    { id: "effective_date", header: "Effective Date" },
    { id: "created_at", header: "Recorded On", filter: createdAtFilter },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      asset: assetLabel(item),
      previous_status: <AssetStatusTag status={item?.previous_status} />,
      new_status: <AssetStatusTag status={item?.new_status} />,
      created_at: formatDate(item?.created_at),
      actions: (
        <FormActions
          actions={[createViewAction(URL_ASSET_STATUS_UPDATES, item?.id)]}
        />
      ),
    }));

  const viewData = (item: any) => ({
    sections: [
      {
        title: "Status Update Details",
        fields: {
          reference: item?.status_update_id,
          asset: assetLabel(item),
          previous_status: <AssetStatusTag status={item?.previous_status} />,
          new_status: <AssetStatusTag status={item?.new_status} />,
          reason: item?.reason,
          effective_date: item?.effective_date,
        },
      },
      { title: "Record", fields: auditFields(item) },
    ],
  });

  return {
    tableTitle: "Asset Status Update",
    tableColumns,
    tableData,
    viewData,
    activityFilter: "asset_status_updates",
    // each update is a point-in-time record: new ones only, no edits
    formRoute: can(CAN_UPDATE_ASSET_STATUS)
      ? getForm(URL_ASSET_STATUS_UPDATES)
      : undefined,
    createTitle: "New Status Update",
    exportData: assetExport(
      API_ASSET_STATUS_UPDATES_EXPORT,
      "asset_status_updates",
    ),
  };
};

export const useAssetStatusUpdatesForm = ({
  form,
  searchParams,
  updateFormValue,
}: TFormHookParams & { searchParams?: Record<string, string> } = {}) => {
  const { search: assetSearch, asset } = useAssetPicker(form?.asset);

  useEffect(() => {
    if (searchParams?.asset) updateFormValue?.("asset", searchParams.asset);
  }, []);

  const blocked = isDisposed(asset);

  const sections: IFormSection[] = [
    {
      title: "Status Update",
      fields: [
        {
          field_name: "asset",
          label: "Asset",
          type: "select",
          search: assetSearch,
          props: { required: true, placeholder: "Search asset by name" },
        },
        {
          field_name: "asset_summary",
          type: "component",
          visible: !!asset,
          component: (
            <AssetSummary
              asset={asset}
              warning={
                blocked
                  ? "A disposed asset's status can no longer be updated."
                  : undefined
              }
            />
          ),
        },
        {
          field_name: "new_status",
          label: "New Status",
          type: "select",
          options_data: toSelectItems(ASSET_STATUSES).filter(
            (item: any) => item.value !== asset?.status,
          ),
          props: { required: true, placeholder: "Select new status" },
        },
        {
          field_name: "effective_date",
          label: "Effective Date",
          type: "date",
          props: { required: true, disableFuture: true },
        },
        {
          field_name: "reason",
          label: "Reason",
          type: "textarea",
          props: { required: true, placeholder: "Reason for the change" },
        },
      ],
    },
  ];

  const onSubmit = (payload: any) => ({
    api: API_ASSET_STATUS_UPDATES,
    params: {
      asset: payload?.asset,
      new_status: payload?.new_status,
      reason: payload?.reason,
      effective_date: payload?.effective_date,
    },
  });

  return {
    sections,
    title: "Asset Status Update",
    redirect: getRegister(URL_ASSET_STATUS_UPDATES),
    onSubmit,
    submitDisabled: blocked,
  };
};
